import React from 'react'
import PropTypes from 'prop-types'
import {Box, Text, Flex} from '@salesforce/retail-react-app/app/components/shared/ui'
import Link from '@salesforce/retail-react-app/app/components/link'
import {FormattedMessage} from 'react-intl'
import {useBrand} from '../../../contexts/brand-context'

const PolicyLink = ({children, href, testId}) => {
    return (
        <Link to={href} data-testid={testId} textDecoration="underline" isExternal={false}>
            {children}
        </Link>
    )
}

const getPolicyLinks = (isGarage) => ({
    cancellationPolicy: (
        <PolicyLink href="/faq.html" testId="order-details-policy-cancellation">
            <FormattedMessage
                defaultMessage="FAQ"
                id="account_order_detail.status.cancellation_policy"
            />
        </PolicyLink>
    ),
    shippingLink: (
        <PolicyLink href="/shipping-policy.html" testId="order-details-policy-shipping">
            {isGarage ? (
                <FormattedMessage
                    defaultMessage="Shipping and Delivery Policy"
                    id="account_order_detail.status.shipping_and_delivery_policy.garage"
                />
            ) : (
                <FormattedMessage
                    defaultMessage="Shipping and Delivery Policy"
                    id="account_order_detail.status.shipping_and_delivery_policy.dynamite"
                />
            )}
        </PolicyLink>
    ),
    returnPolicy: (
        <PolicyLink href="/return-policy.html" testId="order-details-policy-return">
            <FormattedMessage
                defaultMessage="Return Policy"
                id="account_order_detail.status.return_policy"
            />
        </PolicyLink>
    )
})

const getDescriptionMessages = (isGarage) => {
    const policyLinks = getPolicyLinks(isGarage)

    return {
        receivedGarage: {
            id: 'account_order_detail.status.order_received.garage',
            defaultMessage:
                "You can cancel your order or update the shipping address before it's processed. Don't worry, you won't be charged. Questions? See our {cancellationPolicy}.",
            values: {cancellationPolicy: policyLinks.cancellationPolicy}
        },
        receivedDynamite: {
            id: 'account_order_detail.status.order_received.dynamite',
            defaultMessage:
                "You can cancel your order or update the shipping address before it's processed. Don't worry, you won't be charged. Questions? See our {cancellationPolicy}.",
            values: {cancellationPolicy: policyLinks.cancellationPolicy}
        },
        receivedCancelOnlyGarage: {
            id: 'account_order_detail.status.order_received_cancel_only.garage',
            defaultMessage:
                "You can cancel your order before it's processed. Don't worry, you won't be charged. Questions? See our {cancellationPolicy}.",
            values: {cancellationPolicy: policyLinks.cancellationPolicy}
        },
        receivedCancelOnlyDynamite: {
            id: 'account_order_detail.status.order_received_cancel_only.dynamite',
            defaultMessage:
                "You can cancel your order before it's processed. Don't worry, you won't be charged. Questions? See our {cancellationPolicy}.",
            values: {cancellationPolicy: policyLinks.cancellationPolicy}
        },
        receivedUpdateShippingOnlyGarage: {
            id: 'account_order_detail.status.order_received_update_shipping_only.garage',
            defaultMessage:
                'You can update the shipping address before your order is processed. Questions? See our {cancellationPolicy}.',
            values: {cancellationPolicy: policyLinks.cancellationPolicy}
        },
        receivedUpdateShippingOnlyDynamite: {
            id: 'account_order_detail.status.order_received_update_shipping_only.dynamite',
            defaultMessage:
                'You can update the shipping address before your order is processed. Questions? See our {cancellationPolicy}.',
            values: {cancellationPolicy: policyLinks.cancellationPolicy}
        },
        processingGarage: {
            id: 'account_order_detail.status.order_processing.garage',
            defaultMessage:
                'At this point, it cannot be cancelled or changed. Questions? See our {cancellationPolicy}.',
            values: {cancellationPolicy: policyLinks.cancellationPolicy}
        },
        processingDynamite: {
            id: 'account_order_detail.status.order_processing.dynamite',
            defaultMessage:
                'At this point, it cannot be cancelled or changed. Questions? See our {cancellationPolicy}.',
            values: {cancellationPolicy: policyLinks.cancellationPolicy}
        },
        shippedGarage: {
            id: 'account_order_detail.status.order_shipped.garage',
            defaultMessage:
                'Your order has left our facility and is on its way to you! At this point, it cannot be cancelled or changed. Questions? See our {shippingAndDeliveryPolicy}.',
            values: {
                shippingAndDeliveryPolicy: policyLinks.shippingLink
            }
        },
        shippedDynamite: {
            id: 'account_order_detail.status.order_shipped.dynamite',
            defaultMessage:
                'Your order has left our facility and is on its way to you! At this point, it cannot be cancelled or changed. Questions? See our {shippingAndDeliveryPolicy}.',
            values: {
                shippingAndDeliveryPolicy: policyLinks.shippingLink
            }
        },
        partiallyShippedGarage: {
            id: 'account_order_detail.status.order_partially_shipped.garage',
            defaultMessage:
                'Part of your order has left our facility and is on its way to you! At this point, it cannot be cancelled or changed. Questions? See our {shippingAndDeliveryPolicy}.',
            values: {
                shippingAndDeliveryPolicy: policyLinks.shippingLink
            }
        },
        partiallyShippedDynamite: {
            id: 'account_order_detail.status.order_partially_shipped.dynamite',
            defaultMessage:
                'Part of your order has left our facility and is on its way to you! At this point, it cannot be cancelled or changed. Questions? See our {shippingAndDeliveryPolicy}.',
            values: {
                shippingAndDeliveryPolicy: policyLinks.shippingLink
            }
        },
        deliveredGarage: {
            id: 'account_order_detail.status.order_delivered.garage',
            defaultMessage:
                'Select the items you’d like to send back and print your return label when you’re ready. Questions? See our {returnPolicy}.',
            values: {returnPolicy: policyLinks.returnPolicy}
        },
        deliveredDynamite: {
            id: 'account_order_detail.status.order_delivered.dynamite',
            defaultMessage:
                'Select the items you’d like to send back and print your return label when you’re ready. Questions? See our {returnPolicy}.',
            values: {returnPolicy: policyLinks.returnPolicy}
        },
        partiallyDeliveredGarage: {
            id: 'account_order_detail.status.order_partially_delivered.garage',
            defaultMessage:
                'If you need to make a return, select the items you’d like to send back and print your return label. Questions? See our {returnPolicy}.',
            values: {returnPolicy: policyLinks.returnPolicy}
        },
        partiallyDeliveredDynamite: {
            id: 'account_order_detail.status.order_partially_delivered.dynamite',
            defaultMessage:
                'If you need to make a return, select the items you’d like to send back and print your return label. Questions? See our {returnPolicy}.',
            values: {returnPolicy: policyLinks.returnPolicy}
        },
        partiallyCancelledGarage: {
            id: 'account_order_detail.status.order_partially_cancelled.garage',
            defaultMessage:
                'If you need to make a return, select the items you’d like to send back and print your return label. Questions? See our {returnPolicy}.',
            values: {returnPolicy: policyLinks.returnPolicy}
        },
        partiallyCancelledDynamite: {
            id: 'account_order_detail.status.order_partially_cancelled.dynamite',
            defaultMessage:
                'If you need to make a return, select the items you’d like to send back and print your return label. Questions? See our {returnPolicy}.',
            values: {returnPolicy: policyLinks.returnPolicy}
        },
        deliveredFinalSaleGarage: {
            id: 'account_order_detail.status.order_delivered_final_sale.garage',
            defaultMessage:
                'Your package has been delivered and this purchase is Final Sale. At this stage, it can no longer be returned. Questions? See {returnPolicy}.',
            values: {returnPolicy: policyLinks.returnPolicy}
        },
        deliveredFinalSaleDynamite: {
            id: 'account_order_detail.status.order_delivered_final_sale.dynamite',
            defaultMessage:
                'Your package has been delivered and this purchase is Final Sale. At this stage, it can no longer be returned. Questions? See {returnPolicy}.',
            values: {returnPolicy: policyLinks.returnPolicy}
        },
        deliveredReturnClosedGarage: {
            id: 'account_order_detail.status.order_delivered_return_closed.garage',
            defaultMessage:
                'Your order was delivered, but the return window is now closed. Unfortunately, at this point, you can no longer make a return. Questions? See our {returnPolicy}.',
            values: {returnPolicy: policyLinks.returnPolicy}
        },
        deliveredReturnClosedDynamite: {
            id: 'account_order_detail.status.order_delivered_return_closed.dynamite',
            defaultMessage:
                'Your order was delivered, but the return window is now closed. Unfortunately, at this point, you can no longer make a return. Questions? See our {returnPolicy}.',
            values: {returnPolicy: policyLinks.returnPolicy}
        },
        cancelledGarage: {
            id: 'account_order_detail.status.order_partially_cancelled_by_system.garage',
            defaultMessage:
                'The pending charge will be removed from your payment method in a few business days, depending on your bank.'
        },
        cancelledDynamite: {
            id: 'account_order_detail.status.order_partially_cancelled_by_system.dynamite',
            defaultMessage:
                'The pending charge will be removed from your payment method in a few business days, depending on your bank.'
        },
        partiallyCancelledByCustomerGarage: {
            id: 'account_order_detail.action.text.partially_cancelled_by_customer.garage',
            defaultMessage:
                'Unfortunately, part of your order could not be shipped. The pending charge will be removed from your payment method in a few business days, depending on your bank.',
        },
        partiallyCancelledByCustomerDynamite: {
            id: 'account_order_detail.action.text.partially_cancelled_by_customer.garage',
            defaultMessage:
                'Unfortunately, part of your order could not be shipped. The pending charge will be removed from your payment method in a few business days, depending on your bank.',
        },
        refundedGarage: {
            id: 'account_order_detail.action.text.refunded.garage',
            defaultMessage:
                'Your refund may take a few business days to appear, depending on your bank. Questions? See our {returnPolicy}.',
            values: {returnPolicy: policyLinks.returnPolicy}
        },
        refundedDynamite: {
            id: 'account_order_detail.action.text.refunded.dynamite',
            defaultMessage:
                'Your refund may take a few business days to appear, depending on your bank. Questions? See our {returnPolicy}.',
            values: {returnPolicy: policyLinks.returnPolicy}
        },
        partiallyReturnedGarage: {
            id: 'account_order_detail.action.text.partially_returned.garage',
            defaultMessage:
                'Your refund may take a few business days to appear, depending on your bank. Questions? See our {returnPolicy}.',
            values: {returnPolicy: policyLinks.returnPolicy}
        },
        partiallyReturnedDynamite: {
            id: 'account_order_detail.action.text.partially_returned.dynamite',
            defaultMessage:
                'Your refund may take a few business days to appear, depending on your bank. Questions? See our {returnPolicy}.',
            values: {returnPolicy: policyLinks.returnPolicy}
        },
        pendingReturnGarage: {
            id: 'account_order_detail.action.text.pending_return.garage',
            defaultMessage:
                "We'll process your refund as soon as we receive and inspect your item(s). Questions? See our {returnPolicy}.",
            values: {returnPolicy: policyLinks.returnPolicy}
        },
        pendingReturnDynamite: {
            id: 'account_order_detail.action.text.pending_return.dynamite',
            defaultMessage:
                "We'll process your refund as soon as we receive and inspect your item(s). Questions? See our {returnPolicy}.",
            values: {returnPolicy: policyLinks.returnPolicy}
        },
        pickedUpGarage: {
            id: 'account_order_detail.status.order_delivered.garage',
            defaultMessage:
                'Select the items you’d like to send back and print your return label when you’re ready. Questions? See our {returnPolicy}.',
            values: {returnPolicy: policyLinks.returnPolicy}
        },
        pickedUpDynamite: {
            id: 'account_order_detail.status.order_delivered.dynamite',
            defaultMessage:
                'Select the items you’d like to send back and print your return label when you’re ready. Questions? See our {returnPolicy}.',
            values: {returnPolicy: policyLinks.returnPolicy}
        },
        bopisProcessingGarage: {
            id: 'account_order_detail.status.order_pick_up_instore_processing.garage',
            defaultMessage:
                'Your order is being prepared and will be ready for pick-up in 2 hours. At this point, it cannot be cancelled or changed. Questions? See our {cancellationPolicy}.',
            values: {cancellationPolicy: policyLinks.cancellationPolicy}
        },
        bopisProcessingDynamite: {
            id: 'account_order_detail.status.order_pick_up_instore_processing.garage',
            defaultMessage:
                'Your order is being prepared and will be ready for pick-up in 2 hours. At this point, it cannot be cancelled or changed. Questions? See our {cancellationPolicy}.',
            values: {cancellationPolicy: policyLinks.cancellationPolicy}
        }
    }
}

const getDescriptionContent = (descriptionKey, isGarage) => {
    const DESCRIPTION_MESSAGES = getDescriptionMessages(isGarage)

    if (descriptionKey === 'partiallyCancelledBySystem') {
        return (
            <Flex direction="column" gap={4}>
                <Text>
                    {isGarage ? (
                        <FormattedMessage
                            id="account_order_detail.status.order_partially_cancelled_by_system.garage"
                            defaultMessage="The pending charge will be removed from your payment method in a few business days, depending on your bank. "
                        />
                    ) : (
                        <FormattedMessage
                            id="account_order_detail.status.order_partially_cancelled_by_system.dynamite"
                            defaultMessage="The pending charge will be removed from your payment method in a few business days, depending on your bank. "
                        />
                    )}
                </Text>

                <Text>
                    {isGarage ? (
                        <FormattedMessage {...DESCRIPTION_MESSAGES.partiallyCancelledGarage} />
                    ) : (
                        <FormattedMessage {...DESCRIPTION_MESSAGES.partiallyCancelledDynamite} />
                    )}
                </Text>
            </Flex>
        )
    }

    let message

    if (descriptionKey === 'received') {
        message = DESCRIPTION_MESSAGES[isGarage ? 'receivedGarage' : 'receivedDynamite']
    } else if (descriptionKey === 'processing') {
        message = DESCRIPTION_MESSAGES[isGarage ? 'processingGarage' : 'processingDynamite']
    } else if (descriptionKey === 'shipped') {
        message = DESCRIPTION_MESSAGES[isGarage ? 'shippedGarage' : 'shippedDynamite']
    } else if (descriptionKey === 'partiallyShipped') {
        message = DESCRIPTION_MESSAGES[isGarage ? 'partiallyShippedGarage' : 'partiallyShippedDynamite']
    } else if (descriptionKey === 'delivered') {
        message = DESCRIPTION_MESSAGES[isGarage ? 'deliveredGarage' : 'deliveredDynamite']
    } else if (descriptionKey === 'deliveredFinalSale') {
        message = DESCRIPTION_MESSAGES[isGarage ? 'deliveredFinalSaleGarage' : 'deliveredFinalSaleDynamite']
    } else if (descriptionKey === 'deliveredReturnClosed') {
        message = DESCRIPTION_MESSAGES[isGarage ? 'deliveredReturnClosedGarage' : 'deliveredReturnClosedDynamite']
    } else if (descriptionKey === 'cancelled') {
        message = DESCRIPTION_MESSAGES[isGarage ? 'cancelledGarage' : 'cancelledDynamite']
    } else if (descriptionKey === 'refunded') {
        message = DESCRIPTION_MESSAGES[isGarage ? 'refundedGarage' : 'refundedDynamite']
    } else if (descriptionKey === 'partiallyReturned') {
        message = DESCRIPTION_MESSAGES[isGarage ? 'partiallyReturnedGarage' : 'partiallyReturnedDynamite']
    } else if (descriptionKey === 'pendingReturn') {
        message = DESCRIPTION_MESSAGES[isGarage ? 'pendingReturnGarage' : 'pendingReturnDynamite']
    } else if (descriptionKey === 'receivedCancelOnly') {
        message = DESCRIPTION_MESSAGES[isGarage ? 'receivedCancelOnlyGarage' : 'receivedCancelOnlyDynamite']
    } else if (descriptionKey === 'receivedUpdateShippingOnly') {
        message = DESCRIPTION_MESSAGES[isGarage ? 'receivedUpdateShippingOnlyGarage' : 'receivedUpdateShippingOnlyDynamite']
    } else if (descriptionKey === 'partiallyDelivered') {
        message = DESCRIPTION_MESSAGES[isGarage ? 'partiallyDeliveredGarage' : 'partiallyDeliveredDynamite']
    } else if (descriptionKey === 'partiallyCancelledByCustomer') {
        message = DESCRIPTION_MESSAGES[isGarage ? 'partiallyCancelledByCustomerGarage' : 'partiallyCancelledByCustomerDynamite']
    } else if (descriptionKey === 'pickedUp') {
        message = DESCRIPTION_MESSAGES[isGarage ? 'pickedUpGarage' : 'pickedUpDynamite']
    } else if (descriptionKey === 'bopisProcessing') {
        message = DESCRIPTION_MESSAGES[isGarage ? 'bopisProcessingGarage' : 'bopisProcessingDynamite']
    } else {
        message = DESCRIPTION_MESSAGES[descriptionKey]
    }

    return message ? <FormattedMessage {...message} /> : null
}

export const DSSActionDescription = ({children, styles}) => {
    return (
        <Box
            data-testid="order-details-description"
            {...styles.descriptionText}
            width={{base: '95%', xl: '458px'}}
        >
            {children}
        </Box>
    )
}

const DSSActionDetails = ({descriptionKey, styles}) => {
    const {isGarage} = useBrand()

    return (
        <DSSActionDescription styles={styles}>
            {getDescriptionContent(descriptionKey, isGarage)}
        </DSSActionDescription>
    )
}

PolicyLink.propTypes = {
    children: PropTypes.node.isRequired,
    href: PropTypes.string.isRequired,
    testId: PropTypes.string.isRequired
}

DSSActionDescription.propTypes = {
    children: PropTypes.node,
    styles: PropTypes.object.isRequired
}

DSSActionDetails.propTypes = {
    descriptionKey: PropTypes.string,
    styles: PropTypes.object.isRequired
}

export default DSSActionDetails
